import { useState, useEffect, useCallback, useRef } from 'react';
import { UseCatalogFormOptions, FormFieldConfig, FormContext } from './types';
import { evaluateValidator } from './validators';

export function isFieldVisible(
  field: FormFieldConfig,
  values: Record<string, any>,
  context: FormContext
): boolean {
  if (field.visible === undefined) return true;
  if (typeof field.visible === 'boolean') return field.visible;
  if (typeof field.visible === 'function') {
    return Boolean(field.visible(values, context));
  }
  return true;
}

export function useCatalogForm<T extends Record<string, any>>({
  initialValues = {} as T,
  fields,
  id,
  loadData,
  onSubmit,
  onSuccess,
  validate,
}: UseCatalogFormOptions<T>) {
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  // Build default empty state (computed once or when fields change)
  const computeDefaultValues = useCallback((): T => {
    const defaults: Record<string, any> = { ...initialValuesRef.current };
    fieldsRef.current.forEach((f) => {
      if (defaults[f.name] === undefined) {
        if (f.type === 'checkbox') {
          defaults[f.name] = false;
        } else if (f.type === 'select_multiple' || f.multiple) {
          defaults[f.name] = [];
        } else {
          defaults[f.name] = '';
        }
      }
    });
    return defaults as T;
  }, []);

  const [values, setValues] = useState<T>(computeDefaultValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(Boolean(id && loadData));
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load existing data if editing when id changes
  useEffect(() => {
    let isMounted = true;
    if (id && loadData) {
      setLoading(true);
      loadData(id)
        .then((data) => {
          if (isMounted && data) {
            setValues((prev) => ({
              ...prev,
              ...data,
              ...(id ? { id } : {}),
            } as T));
          }
        })
        .catch((err) => {
          console.error('Error loading form data:', err);
          if (isMounted) {
            setStatusMessage({ type: 'error', text: 'Error al cargar los datos del registro' });
          }
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [id, loadData]);

  // Validate a single field
  const validateField = useCallback(
    (name: string, val: any, currentValues = values): string | null => {
      const field = fieldsRef.current.find((f) => f.name === name);
      if (!field) return null;

      const context: FormContext = { id, isEdit: Boolean(id) };
      if (!isFieldVisible(field, currentValues, context)) {
        return null;
      }

      // Check validators first (to allow custom required messages)
      if (field.validators && field.validators.length > 0) {
        for (const validator of field.validators) {
          const err = evaluateValidator(validator, val, currentValues, field);
          if (err) return err;
        }
      }

      // Check required fallback
      if (field.required) {
        if (
          val === null ||
          val === undefined ||
          (typeof val === 'string' && val.trim() === '') ||
          (Array.isArray(val) && val.length === 0)
        ) {
          return 'Este campo es obligatorio';
        }
      }
      return null;
    },
    [values, id]
  );

  // Validate entire form
  const validateAll = useCallback(
    (currentValues = values): { isValid: boolean; newErrors: Record<string, string> } => {
      const newErrors: Record<string, string> = {};
      const context: FormContext = { id, isEdit: Boolean(id) };

      // Validate each visible field
      fieldsRef.current.forEach((field) => {
        if (isFieldVisible(field, currentValues, context)) {
          const err = validateField(field.name, currentValues[field.name], currentValues);
          if (err) {
            newErrors[field.name] = err;
          }
        }
      });

      // Run form-level custom validation if defined
      if (validate) {
        const formErrors = validate(currentValues);
        if (formErrors) {
          Object.assign(newErrors, formErrors);
        }
      }

      return {
        isValid: Object.keys(newErrors).length === 0,
        newErrors,
      };
    },
    [values, validateField, validate, id]
  );

  const setFieldValue = useCallback((name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback(
    (name: string, value: any) => {
      let updatedValues: T;
      setValues((prev) => {
        const updated = { ...prev, [name]: value };
        updatedValues = updated;
        // If already touched, validate dynamically
        if (touched[name]) {
          const fieldError = validateField(name, value, updated);
          setErrors((prevErr) => {
            const next = { ...prevErr };
            if (fieldError) next[name] = fieldError;
            else delete next[name];
            return next;
          });
        }
        return updated;
      });
      if (statusMessage) setStatusMessage(null);

      // Invoke field onChange callback if configured
      const fieldConfig = fieldsRef.current.find((f) => f.name === name);
      if (fieldConfig && fieldConfig.onChange) {
        fieldConfig.onChange(value, {
          setFieldValue,
          values: updatedValues! || { ...values, [name]: value },
        });
      }
    },
    [touched, validateField, statusMessage, setFieldValue, values]
  );

  const handleBlur = useCallback(
    (name: string) => {
      setTouched((prev) => ({ ...prev, [name]: true }));
      const fieldError = validateField(name, values[name], values);
      setErrors((prevErr) => {
        const next = { ...prevErr };
        if (fieldError) next[name] = fieldError;
        else delete next[name];
        return next;
      });
    },
    [validateField, values]
  );

  const resetForm = useCallback(() => {
    setValues(computeDefaultValues());
    setErrors({});
    setTouched({});
    setStatusMessage(null);
  }, [computeDefaultValues]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent): Promise<boolean> => {
      if (e && e.preventDefault) e.preventDefault();

      const context: FormContext = { id, isEdit: Boolean(id) };

      // Mark all visible fields as touched
      const allTouched: Record<string, boolean> = {};
      fieldsRef.current.forEach((f) => {
        if (isFieldVisible(f, values, context)) {
          allTouched[f.name] = true;
        }
      });
      setTouched(allTouched);

      // Validate all
      const { isValid, newErrors } = validateAll();
      setErrors(newErrors);

      if (!isValid) {
        setStatusMessage({
          type: 'error',
          text: 'Por favor complete correctamente todos los campos obligatorios.',
        });
        return false;
      }

      setSubmitting(true);
      setStatusMessage(null);

      try {
        const payload: any = { ...values };
        if (id) {
          payload.id = id;
        }

        const res = await onSubmit(payload);
        setSubmitting(false);

        if (res && res.error) {
          setStatusMessage({
            type: 'error',
            text: res.errMsg || 'Error al guardar los cambios.',
          });
          return false;
        }

        setStatusMessage({
          type: 'success',
          text: 'Registro guardado exitosamente.',
        });

        if (onSuccess) {
          onSuccess(res);
        }
        return true;
      } catch (err) {
        console.error('Submit error:', err);
        setSubmitting(false);
        setStatusMessage({
          type: 'error',
          text: (err as any)?.message || 'Ocurrió un error inesperado al procesar la solicitud.',
        });
        return false;
      }
    },
    [validateAll, values, id, onSubmit, onSuccess]
  );

  return {
    values,
    setValues,
    errors,
    touched,
    loading,
    submitting,
    statusMessage,
    setStatusMessage,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm,
    handleSubmit,
    validateField,
    validateAll,
  };
}
